import { formatMoney } from '@theme/money-formatting';

const CART_WRAPPER_SELECTOR = '#candyrack-slider-cart-wrapper';
const CONTENT_SELECTOR = '.candyrack-slider-cart-content-sticky';
const ITEMS_SELECTOR = '.candyrack-slider-cart-items-wrapper';
const PROGRESS_SELECTOR = '[data-candyrack-cart-progress]';
const TEMPLATE_SELECTOR = '#candyrack-cart-progress-template';
const REFRESH_DELAY = 150;

let refreshTimer;
let refreshInFlight = false;
let refreshQueued = false;
let cartNeedsRefresh = true;
let cartStateSignature = '';
let observedCartWrapper;
let cartObserver;

/**
 * Match the server-rendered thousands separator used by Liquid's money filter.
 *
 * @param {HTMLElement} progress
 * @param {number} value
 * @returns {string}
 */
function formatCartMoney(progress, value) {
  const moneyFormat = progress.dataset.moneyFormat || '{{amount}}';
  const currency = progress.dataset.cartCurrency || 'USD';
  const formatted = formatMoney(value, moneyFormat, currency);

  if (!moneyFormat.includes('no_decimals')) return formatted;

  const reference = Array.from(progress.querySelectorAll('.cart-progress__tier-amount'))
    .map((element) => element.textContent || '')
    .find((text) => /\d([^\d])\d{3}(?:[^\d]|$)/.test(text));
  const referenceSeparator = reference?.match(/\d([^\d])\d{3}(?:[^\d]|$)/)?.[1];

  if (!referenceSeparator) return formatted;

  return formatted.replace(
    /(\d)([^\d])(?=\d{3}(?:[^\d]|$))/g,
    (_, digit, separator) => `${digit}${separator === ',' || separator === '.' ? referenceSeparator : separator}`
  );
}

/**
 * Give each cart reward an equal visual stage while retaining its real threshold.
 *
 * @param {number} total
 * @param {number} shippingThreshold
 * @param {number} giftThreshold
 * @param {number} firstTierPosition
 * @returns {number}
 */
function calculateProgressPercentage(total, shippingThreshold, giftThreshold, firstTierPosition) {
  const hasGiftTier = giftThreshold > 0 && giftThreshold !== shippingThreshold;
  const maximumThreshold = Math.max(shippingThreshold, hasGiftTier ? giftThreshold : 0);

  if (maximumThreshold <= 0) return 0;
  if (!hasGiftTier) return Math.min(Math.max((total / maximumThreshold) * 100, 0), 100);

  const minimumThreshold = Math.min(shippingThreshold, giftThreshold);
  const firstStageEnd = Math.min(Math.max(firstTierPosition, 0), 100);

  if (total <= minimumThreshold) {
    return Math.min(Math.max((total / minimumThreshold) * firstStageEnd, 0), firstStageEnd);
  }

  const thresholdRange = maximumThreshold - minimumThreshold;
  const progressAboveFirstTier = ((total - minimumThreshold) / thresholdRange) * (100 - firstStageEnd);

  return Math.min(Math.max(firstStageEnd + progressAboveFirstTier, firstStageEnd), 100);
}

/**
 * @param {HTMLElement} progress
 * @param {number} total
 * @param {number} itemCount
 */
function updateProgress(progress, total, itemCount) {
  const shippingThreshold = Number(progress.dataset.freeShippingThreshold || 0);
  const giftThreshold = Number(progress.dataset.giftThreshold || 0);
  const firstTierPosition = Number(progress.dataset.firstTierPosition || 50);
  const hasGiftTier = giftThreshold > 0 && giftThreshold !== shippingThreshold;
  const shippingRemaining = shippingThreshold - total;
  const giftRemaining = giftThreshold - total;
  const percentage = calculateProgressPercentage(total, shippingThreshold, giftThreshold, firstTierPosition);
  const message = progress.querySelector('.cart-progress__message');
  const track = progress.querySelector('.cart-progress__track');
  const shippingMarker = progress.querySelector('[data-cart-progress-marker="shipping"]');
  const giftMarker = progress.querySelector('[data-cart-progress-marker="gift"]');
  const container = progress.closest(PROGRESS_SELECTOR);

  if (container instanceof HTMLElement) {
    container.hidden = itemCount === 0;
  }

  if (!(message instanceof HTMLElement) || !(track instanceof HTMLElement)) return;

  let nextTier = '';
  let nextTierRemaining = 0;

  if (shippingRemaining > 0) {
    nextTier = 'shipping';
    nextTierRemaining = shippingRemaining;
  }

  if (hasGiftTier && giftRemaining > 0 && (nextTierRemaining === 0 || giftRemaining < nextTierRemaining)) {
    nextTier = 'gift';
    nextTierRemaining = giftRemaining;
  }

  if (nextTierRemaining > 0) {
    const amount = formatCartMoney(progress, nextTierRemaining);
    const template =
      nextTier === 'gift'
        ? progress.dataset.giftRemainingTemplate
        : progress.dataset.shippingRemainingTemplate;

    message.innerHTML = (template || '__AMOUNT__').replace('__AMOUNT__', amount);
  } else {
    message.textContent = hasGiftTier
      ? progress.dataset.allUnlockedMessage || ''
      : progress.dataset.shippingUnlockedMessage || '';
  }

  track.style.setProperty('--cart-progress-percentage', `${percentage}%`);
  track.setAttribute('aria-valuenow', String(Math.round(percentage)));
  track.setAttribute('aria-valuetext', message.textContent || '');
  shippingMarker?.classList.toggle('cart-progress__marker--unlocked', shippingRemaining <= 0);
  giftMarker?.classList.toggle('cart-progress__marker--unlocked', giftRemaining <= 0);
}

async function refreshProgress() {
  const progress = document.querySelector(`${CART_WRAPPER_SELECTOR} ${PROGRESS_SELECTOR} [data-cart-progress-tiers]`);

  if (!(progress instanceof HTMLElement)) return;

  if (refreshInFlight) {
    refreshQueued = true;
    return;
  }

  refreshInFlight = true;
  try {
    const response = await fetch(progress.dataset.cartUrl || '/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) return;

    const cart = await response.json();

    cartNeedsRefresh = false;
    updateProgress(progress, Number(cart.total_price || 0), Number(cart.item_count || 0));
  } catch {
    // Keep the server-rendered state if the cart endpoint is temporarily unavailable.
  } finally {
    refreshInFlight = false;

    if (refreshQueued) {
      refreshQueued = false;
      scheduleRefresh();
    }
  }
}

function scheduleRefresh() {
  cartNeedsRefresh = true;
  window.clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(refreshProgress, REFRESH_DELAY);
}

/**
 * Track only data that represents cart contents. Candy Rack frequently changes
 * presentation attributes inside the drawer without changing the cart.
 *
 * @param {HTMLElement} wrapper
 * @returns {string}
 */
function getCartStateSignature(wrapper) {
  const items = wrapper.querySelector(ITEMS_SELECTOR);
  if (!(items instanceof HTMLElement)) return '';

  const state = [];

  for (const item of items.querySelectorAll('.candyrack-slider-cart-item-container')) {
    const quantityInput = item.querySelector(
      '.candyrack-slider-cart-item-product-details-quantity-input'
    );

    state.push({
      product:
        item
          .querySelector('.candyrack-slider-cart-item-image-container a')
          ?.getAttribute('href') || '',
      variant:
        item
          .querySelector('.candyrack-slider-cart-item-product-details-variant')
          ?.textContent?.trim() || '',
      quantity: quantityInput instanceof HTMLInputElement ? quantityInput.value : '',
      price:
        item
          .querySelector('.candyrack-slider-cart-item-product-details-price')
          ?.textContent?.trim() || '',
    });
  }

  return JSON.stringify(state);
}

/**
 * @param {HTMLElement} wrapper
 */
function observeCart(wrapper) {
  if (observedCartWrapper === wrapper) return;

  cartObserver?.disconnect();
  observedCartWrapper = wrapper;
  const nextCartStateSignature = getCartStateSignature(wrapper);

  if (
    cartStateSignature !== '' &&
    nextCartStateSignature !== '' &&
    cartStateSignature !== nextCartStateSignature
  ) {
    scheduleRefresh();
  }

  cartStateSignature = nextCartStateSignature;
  cartObserver = new MutationObserver(() => {
    mountProgress();

    const nextSignature = getCartStateSignature(wrapper);
    if (nextSignature === '' || nextSignature === cartStateSignature) return;

    cartStateSignature = nextSignature;
    scheduleRefresh();
  });
  cartObserver.observe(wrapper, {
    attributes: true,
    attributeFilter: ['value'],
    characterData: true,
    childList: true,
    subtree: true,
  });
}

function mountProgress() {
  const cartWrapper = document.querySelector(CART_WRAPPER_SELECTOR);
  const content = cartWrapper?.querySelector(CONTENT_SELECTOR);
  const items = content?.querySelector(ITEMS_SELECTOR);
  const template = document.querySelector(TEMPLATE_SELECTOR);

  if (
    !(cartWrapper instanceof HTMLElement) ||
    !(content instanceof HTMLElement) ||
    !(items instanceof HTMLElement) ||
    !(template instanceof HTMLTemplateElement)
  ) {
    return;
  }

  observeCart(cartWrapper);

  if (content.querySelector(PROGRESS_SELECTOR)) return;

  const fragment = template.content.cloneNode(true);
  const progressContainer = fragment.firstElementChild;

  if (!(progressContainer instanceof HTMLElement)) return;

  content.insertBefore(fragment, items);

  if (cartNeedsRefresh) scheduleRefresh();
}

const pageObserver = new MutationObserver((records) => {
  const hasCartMutation = records.some((record) => {
    if (
      record.target instanceof Element &&
      (record.target.matches(CART_WRAPPER_SELECTOR) || record.target.closest(CART_WRAPPER_SELECTOR))
    ) {
      return true;
    }

    return Array.from(record.addedNodes).some(
      (node) =>
        node instanceof Element &&
        (node.matches(CART_WRAPPER_SELECTOR) || node.querySelector(CART_WRAPPER_SELECTOR))
    );
  });

  if (hasCartMutation) mountProgress();
});

pageObserver.observe(document.documentElement, { childList: true, subtree: true });
document.addEventListener('cart:update', scheduleRefresh);
mountProgress();
